import { useCallback, useEffect, useRef, useState } from 'react'
import {
  startGeneration,
  startGenerationMock,
  subscribeGenerationStream,
  type CreativeStyle,
  type GenerationErrorDetails,
  type GenerationResult,
  type GenerationStatus,
  type GenerationStatusEvent,
} from '@/api/creatives'
import { track, trackError } from '@/lib/analytics'

const USE_MOCKS = false

// sessionStorage-ключ для активного in-progress job. Используется чтобы при
// рефреше страницы переподключаться к SSE-стриму существующего job вместо
// создания дубля. Имя — по channelUUID, чтобы не пересекать каналы.
const ACTIVE_JOB_KEY_PREFIX = 'tgx-creatives-active-job-'

function loadActiveJobId(channelUUID: string): string | null {
  try {
    return sessionStorage.getItem(`${ACTIVE_JOB_KEY_PREFIX}${channelUUID}`)
  } catch {
    return null
  }
}

function saveActiveJobId(channelUUID: string, jobId: string): void {
  try {
    sessionStorage.setItem(`${ACTIVE_JOB_KEY_PREFIX}${channelUUID}`, jobId)
  } catch {}
}

function clearActiveJobId(channelUUID: string): void {
  try {
    sessionStorage.removeItem(`${ACTIVE_JOB_KEY_PREFIX}${channelUUID}`)
  } catch {}
}

export interface GenerationState {
  status: GenerationStatus | 'idle'
  progress: number
  stage: string
  result: GenerationResult | null
  error: string | null
  errorDetails: GenerationErrorDetails | null
  jobId: string | null
}

const INITIAL_STATE: GenerationState = {
  status: 'idle',
  progress: 0,
  stage: '',
  result: null,
  error: null,
  errorDetails: null,
  jobId: null,
}

export function useCreativeGenerator(channelUUID?: string) {
  const [state, setState] = useState<GenerationState>(INITIAL_STATE)
  const cleanupRef = useRef<(() => void) | null>(null)
  // channelUUID кладём в ref чтобы стабильный onEvent мог его читать без
  // пересоздания callback'а — иначе resume-эффект пересоздавался бы на каждый
  // ре-рендер и SSE-подключение прыгало.
  const channelUUIDRef = useRef<string | undefined>(channelUUID)
  useEffect(() => {
    channelUUIDRef.current = channelUUID
  }, [channelUUID])

  const handleEvent = useCallback((event: GenerationStatusEvent) => {
    // SSE прислал status=failed — это backend-side ошибка Pilotad/AI генерации.
    // Шлём в BetterStack отдельным error-эвентом с подробностями.
    if (event.status === 'failed') {
      const details = event.error_details
      // INSUFFICIENT_POSTS — отдельный funnel-эвент (не ошибка, а ожидаемая
      // развилка: юзер уйдёт в ad-gen-prepare на ручное описание).
      if (details?.code === 'INSUFFICIENT_POSTS') {
        track('insufficient_posts', {
          postsFound: details.posts_found,
          postsRequired: details.posts_required,
          message: `мало постов · ${details.posts_found ?? 0}/${details.posts_required ?? 3}`,
        })
      } else {
        trackError(
          new Error(event.error || details?.message || 'creative generation failed'),
          `creative-gen · code=${details?.code || 'unknown'} · job=${event.job_id || '—'}`,
        )
      }
    }
    // Терминальные статусы — очищаем sessionStorage, чтобы при следующем
    // маунте не пытались резюмировать уже завершённый job.
    if (event.status === 'completed' || event.status === 'failed') {
      const uuid = channelUUIDRef.current
      if (uuid) clearActiveJobId(uuid)
    }
    setState((prev) => {
      // Дедупликация: если status/progress/stage не изменились и нет нового
      // result/error — пропускаем, чтобы не вызывать лишние ре-рендеры.
      // Pilotad шлёт keep-alive эвенты каждые ~1с с тем же state'ом.
      const sameSnapshot =
        prev.status === event.status &&
        prev.progress === event.progress &&
        prev.stage === (event.stage || prev.stage) &&
        !event.result &&
        !event.error_details
      if (sameSnapshot) return prev
      return {
        ...prev,
        status: event.status,
        progress: event.progress,
        stage: event.stage || prev.stage,
        result: event.result || prev.result,
        error: event.error || null,
        errorDetails: event.error_details || null,
        jobId: event.job_id,
      }
    })
  }, [])

  // Resume: при маунте проверяем sessionStorage. Если есть активный jobId для
  // этого канала — переподключаемся к SSE-стриму без создания нового /generate.
  // Это переживает рефреш страницы во время генерации (раньше создавался
  // дубль-job, упирающийся в очередь на cduck-стороне).
  useEffect(() => {
    if (!channelUUID || USE_MOCKS) return
    const savedJobId = loadActiveJobId(channelUUID)
    if (!savedJobId) return
    console.log('[creatives] resuming job', savedJobId, 'for', channelUUID)
    setState({
      status: 'pending',
      progress: 0,
      stage: 'Возобновляем…',
      result: null,
      error: null,
      errorDetails: null,
      jobId: savedJobId,
    })
    if (cleanupRef.current) cleanupRef.current()
    const unsubscribe = subscribeGenerationStream(savedJobId, handleEvent)
    cleanupRef.current = unsubscribe
  }, [channelUUID, handleEvent])

  // На размонтирование хука — закрываем SSE. activeJobId в sessionStorage не
  // трогаем (нужен для следующего resume).
  useEffect(() => {
    return () => {
      if (cleanupRef.current) cleanupRef.current()
    }
  }, [])

  const start = useCallback(
    (opts: { channelUUID: string; style?: CreativeStyle; fallbackText?: string }) => {
      if (cleanupRef.current) cleanupRef.current()
      track('creatives_generation_started', {
        hasFallbackText: !!opts.fallbackText,
        message: opts.fallbackText ? 'генерация по ручному описанию' : 'генерация по постам канала',
      })
      setState({
        status: 'pending',
        progress: 0,
        stage: 'Подготовка…',
        result: null,
        error: null,
        errorDetails: null,
        jobId: null,
      })

      if (USE_MOCKS) {
        const handle = startGenerationMock(opts, handleEvent)
        cleanupRef.current = handle.unsubscribe
        return
      }

      startGeneration(opts)
        .then((res) => {
          saveActiveJobId(opts.channelUUID, res.jobId)
          setState((prev) => ({ ...prev, jobId: res.jobId }))
          const unsubscribe = subscribeGenerationStream(res.jobId, handleEvent)
          cleanupRef.current = unsubscribe
        })
        .catch((e) => {
          trackError(e, 'start-generation')
          setState((prev) => ({
            ...prev,
            status: 'failed',
            error: e instanceof Error ? e.message : 'Ошибка запуска генерации',
            errorDetails: null,
          }))
        })
    },
    [handleEvent],
  )

  const cancel = useCallback(() => {
    if (cleanupRef.current) cleanupRef.current()
    cleanupRef.current = null
    const uuid = channelUUIDRef.current
    if (uuid) clearActiveJobId(uuid)
    setState(INITIAL_STATE)
  }, [])

  return { state, start, cancel }
}
